import { STOREFRONT_CONFIG } from '@core/config/storefront';

/**
 * Application constants
 */

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export const HASURA_URL =
  import.meta.env.VITE_HASURA_URL ||
  'https://hasura.aaracollections.in/v1/graphql';
export const HASURA_SECRET =
  import.meta.env.VITE_HASURA_SECRET || 'Hysas@2026';
export const SHOPID = STOREFRONT_CONFIG.primaryShopId;

export const APP_NAME =
  import.meta.env.VITE_APP_NAME?.trim() || 'Storefront';

/**
 * Header/footer umbrella brand when storefront config mode is `multi-shop`.
 * Single-shop mode uses `mst_shop` name (or `APP_NAME` fallback).
 */
export const MULTI_SHOP_BRAND_NAME = STOREFRONT_CONFIG.multiShopBrandName;

export const MULTI_SHOP_BRAND_LOGO = STOREFRONT_CONFIG.multiShopBrandLogo;

export const SINGLE_SHOP_FALLBACK_LOGO = STOREFRONT_CONFIG.singleShopFallbackLogo;

/** Single-store label override when `mst_shop.shop_name` is missing from GraphQL. */
export const SINGLE_SHOP_DISPLAY_NAME_OVERRIDE =
  STOREFRONT_CONFIG.singleShopDisplayNameOverride;

/** Footer contact / social when DB fields are empty or for multi-shop umbrella. */
export const SITE_FOOTER_FALLBACK = {
  instagram:
    import.meta.env.VITE_SITE_FOOTER_INSTAGRAM?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.instagram,
  whatsapp:
    import.meta.env.VITE_SITE_FOOTER_WHATSAPP?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.whatsapp,
  facebook:
    import.meta.env.VITE_SITE_FOOTER_FACEBOOK?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.facebook,
  youtube:
    import.meta.env.VITE_SITE_FOOTER_YOUTUBE?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.youtube,
  email:
    import.meta.env.VITE_SITE_FOOTER_EMAIL?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.email,
  phone:
    import.meta.env.VITE_SITE_FOOTER_PHONE?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.phone,
  address:
    import.meta.env.VITE_SITE_FOOTER_ADDRESS?.trim() ||
    STOREFRONT_CONFIG.footerDefaults.address,
} as const;

export const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  CATEGORIES: '/categories',
  CART: '/cart',
  ORDERS: '/orders',
  PROFILE: '/profile',
  LOGIN: '/login',
  SIGNUP: '/signup',
  SEARCH: '/search',
  ACCOUNT: '/account',
  CONTACT: '/contact',
  FAQ: '/faq',
  SHIPPING: '/shipping',
  RETURNS: '/returns',
  PRIVACY: '/privacy',
  REELS: '/reels',
} as const;

const parsePositiveInt = (
  value: string | undefined,
  fallback: number
): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const parseNonNegativeInt = (
  value: string | undefined,
  fallback: number
): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

/**
 * GraphQL `limit` for each reels request (`mst_post`). Env: `VITE_REELS_FETCH_LIMIT` (default 8). */
export const REELS_FETCH_LIMIT = parsePositiveInt(
  import.meta.env.VITE_REELS_FETCH_LIMIT,
  8
);

/**
 * GraphQL `offset` for the first reels page. Env: `VITE_REELS_FETCH_INITIAL_OFFSET`. */
export const REELS_FETCH_INITIAL_OFFSET = parseNonNegativeInt(
  import.meta.env.VITE_REELS_FETCH_INITIAL_OFFSET,
  0
);

/**
 * Start preloading when the viewer reaches this 1-based index within each `REELS_FETCH_LIMIT` window (e.g. `6` = sixth reel).
 * Env: `VITE_REELS_PRELOAD_SLOT`.
 */
const REELS_PRELOAD_SLOT_ONE_BASED = parsePositiveInt(
  import.meta.env.VITE_REELS_PRELOAD_SLOT,
  6
);

/**
 * Derived: `activeIndex % REELS_FETCH_LIMIT` must match this value to trigger preloading.
 */
export const REELS_PRELOAD_TRIGGER_INDEX_MOD = Math.min(
  REELS_PRELOAD_SLOT_ONE_BASED - 1,
  Math.max(0, REELS_FETCH_LIMIT - 1)
);

/** Demo/offline reels pool length when GraphQL returns no posts. Env: `VITE_REELS_DEMO_POOL_SIZE`. */
export const REELS_DEMO_POOL_SIZE = parsePositiveInt(
  import.meta.env.VITE_REELS_DEMO_POOL_SIZE,
  32
);

export const R2_PUBLIC_URL =
  import.meta.env.VITE_R2_PUBLIC_URL ||
  'https://pub-73dd9f9359f04b7eb6bff65894b33c65.r2.dev';
